import { createContext, useEffect, useRef, useContext } from 'react'

import { RegionContext } from './RegionContext'
import { TelemetryContext } from './TelemetryContext'

import { GLOBAL_EVENTS } from '../constants/telemetryEvents'

export const MouseEventsContext = createContext()

export const MouseEventsProvider = ({ children }) => {
  const { getRegionName } = useContext(RegionContext)
  const { logTelemetryEvent } = useContext(TelemetryContext)

  const lastMouseLog = useRef(0)

  // Window resize listener
  useEffect(() => {
    let resizeTimeout = null

    const handleResize = () => {
      // Debounce to avoid spam during drag
      if (resizeTimeout) clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(() => {
        logTelemetryEvent(GLOBAL_EVENTS.WINDOW_RESIZE, {
          width: window.innerWidth,
          height: window.innerHeight,
          timestamp: Date.now()
        })
      }, 250)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      if (resizeTimeout) clearTimeout(resizeTimeout)
    }
  }, [logTelemetryEvent])

  // Mouse and visibility listeners
  useEffect(() => {
    const handleMouseMove = (e) => {
      const now = Date.now()
      if (now - lastMouseLog.current > 50) {
        const container = document.querySelector('.ws-content')
        if (!container) return

        const rect = container.getBoundingClientRect()

        const normalizedX = Number(((e.clientX - rect.left) / rect.width).toFixed(4))
        const normalizedY = Number(((e.clientY - rect.top) / rect.height).toFixed(4))

        const region = getRegionName(normalizedX, normalizedY)

        logTelemetryEvent(GLOBAL_EVENTS.MOUSE_MOVE, {
          x: normalizedX,
          y: normalizedY,
          region: region
        })
        lastMouseLog.current = now
      }
    }

    const handleClick = (e) => {
      const container = document.querySelector('.ws-content')
      if (!container) return

      const rect = container.getBoundingClientRect()

      const normalizedX = Number(((e.clientX - rect.left) / rect.width).toFixed(4))
      const normalizedY = Number(((e.clientY - rect.top) / rect.height).toFixed(4))

      const region = getRegionName(normalizedX, normalizedY)

      logTelemetryEvent(GLOBAL_EVENTS.MOUSE_CLICK, {
        x: normalizedX,
        y: normalizedY,
        tag: e.target.tagName,
        className: e.target.className,
        region: region
      })
    }

    const handleVisibility = () => {
      logTelemetryEvent(GLOBAL_EVENTS.TAB_STATE, {
        visible: !document.hidden,
        timestamp: Date.now()  
      })
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('click', handleClick)
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('click', handleClick)
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [logTelemetryEvent, getRegionName])

  return (
    <MouseEventsContext.Provider value={{}}>
      {children}
    </MouseEventsContext.Provider>
  )
}